import { SITE } from "@/config/site";

// ============================================================================
// CONVERSION TRACKING — GA4 / Google Ads / Meta Pixel ready
// ----------------------------------------------------------------------------
// • Every event is pushed to window.dataLayer (GTM-ready), to gtag when a GA4
//   Measurement ID is configured, and beaconed to our own /api/events store
//   (first-party data the Analytics Agent reasons over).
// • UTM parameters are captured on landing, persisted for the session, and
//   forwarded to the BookMyShow outbound URL.
// • Traffic source is classified: google / instagram / facebook / zepto /
//   youtube / whatsapp / direct / referral:<host>.
// • We measure: source -> landing page -> engagement -> BookMyShow click.
//   Completed BookMyShow purchases are NOT visible to us — never claim they are.
// ============================================================================

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const UTM_STORE = "cah_utm";
const SOURCE_STORE = "cah_source";
const VISITOR_STORE = "cah_vid";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function safeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

export function getVisitorId() {
  let id = null;
  try {
    id = localStorage.getItem(VISITOR_STORE);
  } catch {
    /* ignore */
  }
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      localStorage.setItem(VISITOR_STORE, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

export function getStoredUtm() {
  try {
    return JSON.parse(safeGet(UTM_STORE) || "{}");
  } catch {
    return {};
  }
}

function classify(raw) {
  const s = (raw || "").toLowerCase();
  if (!s) return null;
  if (s.includes("google")) return "google";
  if (s.includes("instagram") || s === "ig") return "instagram";
  if (s.includes("facebook") || s.includes("meta") || s === "fb") return "facebook";
  if (s.includes("zepto")) return "zepto";
  if (s.includes("youtube") || s === "yt") return "youtube";
  if (s.includes("whatsapp") || s === "wa") return "whatsapp";
  return s;
}

function detectSource(utmSource) {
  const fromUtm = classify(utmSource);
  if (fromUtm) return fromUtm;
  const ref = document.referrer;
  if (!ref) return "direct";
  try {
    return classify(new URL(ref).hostname) || `referral:${new URL(ref).hostname}`;
  } catch {
    return "direct";
  }
}

export function getSource() {
  return safeGet(SOURCE_STORE) || "direct";
}

function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const found = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) found[k] = v;
  });
  if (Object.keys(found).length) {
    safeSet(UTM_STORE, JSON.stringify({ ...getStoredUtm(), ...found }));
  }
  if (!safeGet(SOURCE_STORE)) {
    safeSet(SOURCE_STORE, detectSource(found.utm_source));
  }
}

function beacon(name, payload) {
  try {
    fetch(`${API_BASE}/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event: name,
        params: payload,
        path: window.location.pathname,
        visitor_id: getVisitorId(),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* beacon is best-effort */
  }
}

export function trackEvent(name, params = {}) {
  const payload = { ...getStoredUtm(), traffic_source: getSource(), ...params };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload });
  if (typeof window.gtag === "function") window.gtag("event", name, payload);
  if (window.console) console.info("[cah:track]", name, payload);
  beacon(name, payload);
}

export function initAnalytics() {
  captureAttribution();
  window.dataLayer = window.dataLayer || [];
  // Single shared Google tag (gtag.js) — loaded once, globally, only when at
  // least one Google ID is configured. GA4 and Google Ads share this one
  // script tag, so a duplicate Google tag can never be created.
  const ga4Id = SITE.ga4MeasurementId;
  const adsId = SITE.googleAdsConversionId;
  if ((ga4Id || adsId) && !window.__cahGtagLoaded) {
    window.__cahGtagLoaded = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id || adsId}`;
    document.head.appendChild(s);
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    if (ga4Id) window.gtag("config", ga4Id, { send_page_view: false });
    if (adsId) window.gtag("config", adsId);
  }
  trackEvent("page_view", { page_path: window.location.pathname });
}

export function withUtm(url) {
  const utm = getStoredUtm();
  const keys = Object.keys(utm);
  if (!keys.length) return url;
  try {
    const u = new URL(url);
    keys.forEach((k) => u.searchParams.set(k, utm[k]));
    return u.toString();
  } catch {
    return url;
  }
}

export function trackOutbound(destination, extra = {}) {
  trackEvent("outbound_click", { destination, ...extra });

  // Google Ads conversion — "BookMyShow Ticket Intent" (AW-18404460770).
  // trackOutbound("bookmyshow", ...) is the one call every BookMyShow CTA on
  // the site already makes on click — openBooking() below (used by every
  // BookButton: TicketsV2, StickyCta, FinalCta, plus the PerformersV2 teaser)
  // and the Footer's own BookMyShow link both go through here. Firing the
  // conversion in this single spot covers all of them without touching any
  // component and without double-firing. Uses the one Google tag already
  // loaded by initAnalytics() - no second tag, no separate script.
  if (destination === "bookmyshow" && typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: "AW-18404460770/qTIYCMn3o_AcEOKR98dE",
      event_callback: function () {},
    });
  }
}

export function openBooking(location) {
  trackEvent("book_ticket_click", { cta_location: location });
  if (location === "hero") trackEvent("hero_cta_click");
  if (location === "tickets") trackEvent("ticket_section_book_ticket_click");
  if (location === "final") trackEvent("final_book_ticket_click");
  trackOutbound("bookmyshow", { cta_location: location });
  window.open(withUtm(SITE.bookMyShowUrl), "_blank", "noopener,noreferrer");
}

// ============================================================================
// CHITTI AI — every "Ask Chitti" / "Chat with Chitti AI" entry point on the
// site calls this. Uses SITE.chittiWhatsappNumber — the number the Chitti AI
// (WATI) agent is actually integrated on — which is deliberately separate
// from SITE.whatsappNumber (used by the footer/social "enquiries" links and
// the /artist stall-enquiry link; those are untouched). No backend/WATI
// config change here — the agent is already live on this number today. The
// only new thing is a pre-filled ticket-intent message and its own
// trackOutbound destination ("whatsapp-chitti-ai") so this journey is
// visible separately in analytics from the plain "whatsapp-enquiries"
// links — the underlying "whatsapp_click" event name is unchanged, so
// nothing that already reports on that event breaks; cta_location/intent
// are additive.
// ============================================================================
export function openChittiChat(location, message) {
  const text = message || SITE.chittiPrefillMessage;
  trackEvent("whatsapp_click", { cta_location: location, intent: "chitti_ai" });
  trackOutbound("whatsapp-chitti-ai", { cta_location: location });
  const url = `https://wa.me/${SITE.chittiWhatsappNumber}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function initScrollTracking() {
  let fired50 = false;
  let fired90 = false;
  const onScroll = () => {
    const el = document.documentElement;
    const max = el.scrollHeight - el.clientHeight;
    if (max <= 0) return;
    const pct = (el.scrollTop / max) * 100;
    if (!fired50 && pct >= 50) {
      fired50 = true;
      trackEvent("scroll_depth", { percent: 50 });
      trackEvent("scroll_50");
    }
    if (!fired90 && pct >= 90) {
      fired90 = true;
      trackEvent("scroll_depth", { percent: 90 });
      trackEvent("scroll_90");
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}
